import { useState, useEffect, useRef, useMemo } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Form, Button, Spinner, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import VideoPlayer from "@/components/VideoPlayer";
import { liveApi, ordersApi } from "@/api";
import { axiosInstance } from "@/core/http";
import Table from "@/components/Elements/Table";
import InfiniteScrollSelect from "@/components/Elements/InfiniteScrollSelect";
import { PIPELINE_OPTIONS } from "@/constants";
import { TEAM_COLORS } from "@/constants/teams";

const LiveTab = () => {
  const videoRef = useRef();

  // Broadcasts
  const [broadcasts, setBroadcasts] = useState([]);
  const [isLoadingBroadcasts, setIsLoadingBroadcasts] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastsPage, setBroadcastsPage] = useState(1);
  const [broadcastsCount, setBroadcastsCount] = useState(0);

  // Cameras
  const [cameras, setCameras] = useState([]);
  const [cameraFilter, setCameraFilter] = useState("");

  // Create form
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createArena, setCreateArena] = useState("");
  const [createPlaygroundId, setCreatePlaygroundId] = useState("");
  const [createStart, setCreateStart] = useState("");
  const [createDurationHours, setCreateDurationHours] = useState("");
  const [createDurationMinutes, setCreateDurationMinutes] = useState("");

  // Orders (for color selection)
  const [orders, setOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Cut form
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [matchTime, setMatchTime] = useState("");
  const [homeTeamColor, setHomeTeamColor] = useState("");
  const [guestTeamColor, setGuestTeamColor] = useState("");
  const [pipelinePassing, setPipelinePassing] = useState("broadcasting");
  const [frameTime, setFrameTime] = useState(1);
  const [isCutting, setIsCutting] = useState(false);

  const fetchBroadcasts = async (page = 1) => {
    try {
      setIsLoadingBroadcasts(true);
      const data = await liveApi.getAll(page);
      const items = Array.isArray(data) ? data : (data?.results ?? []);
      const count = data?.count ?? items.length;
      setBroadcastsCount(count);
      setBroadcastsPage(page);
      setBroadcasts((prev) => (page > 1 ? [...prev, ...items] : items));
    } catch {
      toast.error("Failed to fetch broadcasts");
    } finally {
      setIsLoadingBroadcasts(false);
    }
  };

  const loadMore = async () => {
    if (isLoadingBroadcasts || broadcasts.length >= broadcastsCount) return;
    await fetchBroadcasts(broadcastsPage + 1);
  };

  const fetchAllCameras = async () => {
    try {
      const first = await axiosInstance.get("/panel/playground/", { params: { page: 1 } });
      const firstResults = first?.results ?? (Array.isArray(first) ? first : []);
      const count = first?.count ?? firstResults.length;
      const pageSize = firstResults.length || 1;
      const totalPages = Math.ceil(count / pageSize);

      let allResults = [...firstResults];
      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            axiosInstance.get("/panel/playground/", { params: { page: i + 2 } })
          )
        );
        for (const res of remaining) {
          const r = res?.results ?? (Array.isArray(res) ? res : []);
          allResults = allResults.concat(r);
        }
      }
      setCameras(allResults);
    } catch {
      toast.error("Failed to fetch cameras");
    }
  };

  const fetchOrders = async (page, append = false) => {
    try {
      setOrdersLoading(true);
      const data = await ordersApi.getOrdersList(page, "");
      const results = (data?.results ?? []).filter((o) => o.can_assign_video_later);
      const count = data?.count ?? 0;
      const selectedOrderId = selectedOrder?.id ?? null;
      setOrders((prev) => {
        const next = append ? [...prev, ...results] : results;
        setOrdersHasMore(next.length < count);
        if (!append && selectedOrderId !== null) {
          const nextSelectedOrder =
            next.find((order) => String(order.id) === String(selectedOrderId)) ?? null;
          setSelectedOrder(nextSelectedOrder);
        }
        return next;
      });
      setOrdersPage(page);
    } catch {
      toast.error("Failed to fetch orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    if (ordersLoading || !ordersHasMore) return;
    await fetchOrders(ordersPage + 1, true);
  };

  useEffect(() => {
    fetchBroadcasts();
    fetchAllCameras();
    fetchOrders(1, false);
  }, []);

  const formatCameraLabel = (camera, fallback = "") => {
    const arenaName = camera?.arena_name?.trim();
    const name = camera?.name?.trim();

    if (arenaName && name && arenaName !== name) {
      return `${arenaName} / ${name}`;
    }

    return arenaName || name || fallback;
  };

  const getCameraName = (playgroundId) => {
    const cam = cameras.find((c) => c.id === playgroundId);
    return formatCameraLabel(cam, playgroundId);
  };

  const filteredBroadcasts = useMemo(
    () =>
      (cameraFilter
        ? broadcasts.filter((b) => String(b.playground_id) === cameraFilter)
        : broadcasts
      ).slice().sort((a, b) => b.id - a.id),
    [broadcasts, cameraFilter]
  );

  const handleDelete = async (id) => {
    try {
      setIsDeletingId(id);
      await liveApi.delete(id);
      toast.success("Broadcast stopped");
      if (selectedBroadcast?.id === id) setSelectedBroadcast(null);
      fetchBroadcasts(1);
    } catch {
      toast.error("Failed to stop broadcast");
    } finally {
      setIsDeletingId(null);
    }
  };

  const resetCreateForm = () => {
    setCreateArena("");
    setCreatePlaygroundId("");
    setCreateStart("");
    setCreateDurationHours("");
    setCreateDurationMinutes("");
  };

  const handleCreate = async () => {
    if (!createPlaygroundId) {
      toast.warning("Please select a camera");
      return;
    }
    if (!createStart) {
      toast.warning("Please set a start time");
      return;
    }
    try {
      setIsSubmitting(true);
      const hours = Number(createDurationHours) || 0;
      const minutes = Number(createDurationMinutes) || 0;
      const totalSeconds = hours * 3600 + minutes * 60;
      const duration = totalSeconds > 0 ? totalSeconds : undefined;
      await liveApi.create(Number(createPlaygroundId), createStart, duration);
      toast.success("Broadcast created");
      setIsCreating(false);
      resetCreateForm();
    fetchBroadcasts(1);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Failed to create broadcast";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCancel = () => {
    setIsCreating(false);
    resetCreateForm();
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const setCurrentTimeAsStart = () => {
    if (videoRef.current) setStartTime(formatTime(videoRef.current.currentTime));
  };

  const setCurrentTimeAsEnd = () => {
    if (videoRef.current) setEndTime(formatTime(videoRef.current.currentTime));
  };

  const onVideoLoadHandler = () => {
    const ft = videoRef.current.webkitDecodedFrameCount / videoRef.current.duration;
    setFrameTime(ft || 1);
  };

  const cutAndCreate = async () => {
    if (!selectedBroadcast) return;
    if (!selectedOrder) {
      toast.warning("Please select an order.");
      return;
    }
    if (!startTime || !endTime) {
      toast.warning("Please input start and end time correctly.");
      return;
    }
    const numericMatchTime = Number(matchTime);
    if (!numericMatchTime || numericMatchTime <= 0) {
      toast.warning("Please enter a valid match time.");
      return;
    }
    if (!homeTeamColor || !guestTeamColor) {
      toast.warning("Please select both team colors.");
      return;
    }
    try {
      setIsCutting(true);
      await liveApi.cut(
        selectedBroadcast.id,
        selectedOrder.id,
        startTime,
        endTime,
        pipelinePassing,
        homeTeamColor.toLowerCase(),
        guestTeamColor.toLowerCase(),
        numericMatchTime
      );
      toast.success("Cut request sent successfully.");
      setSelectedOrder(null);
      setHomeTeamColor("");
      setGuestTeamColor("");
      fetchOrders(1, false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong";
      toast.error(errorMessage);
    } finally {
      setIsCutting(false);
    }
  };

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
    },
    {
      header: "Camera",
      cell: (props) => getCameraName(props.row.original.playground_id),
    },
    {
      accessorKey: "start",
      header: "Start",
      cell: (info) =>
        info.getValue()
          ? new Date(info.getValue()).toLocaleString("en-US", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "UTC",
              timeZoneName: "short",
            })
          : "—",
    },
    {
      accessorKey: "end",
      header: "End",
      cell: (info) =>
        info.getValue()
          ? new Date(info.getValue()).toLocaleString("en-US", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              hour12: true,
              timeZone: "UTC",
              timeZoneName: "short",
            })
          : "—",
    },
    {
      accessorKey: "url",
      header: "URL",
      cell: (props) =>
        props.getValue() ? (
          <a href={props.getValue()} rel="noreferrer" target="_blank">
            link
          </a>
        ) : (
          "—"
        ),
    },
    {
      header: "Actions",
      cell: (props) => {
        const broadcast = props.row.original;
        const isDeleting = isDeletingId === broadcast.id;
        return (
          <div className="tw-flex tw-gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setSelectedBroadcast(null);
                setSelectedOrder(null);
                setHomeTeamColor("");
                setGuestTeamColor("");
                setTimeout(() => setSelectedBroadcast(broadcast), 0);
              }}
            >
              View
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={isDeleting}
              onClick={() => handleDelete(broadcast.id)}
            >
              {isDeleting ? (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              ) : (
                <FontAwesomeIcon icon={["fas", "stop"]} />
              )}
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: filteredBroadcasts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="tw-flex-1 tw-overflow-auto tw-py-3 tw-px-4">
      <div className="tw-h-full tw-flex tw-gap-2 tw-flex-col md:tw-flex-row">
        {/* Left panel */}
        <div className="tw-flex-1 d-flex flex-column gap-3 h-100">
          {/* Controls row */}
          <div className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap">
            <Form.Select
              className="tw-w-auto"
              value={cameraFilter}
              onChange={(e) => setCameraFilter(e.target.value)}
            >
              <option value="">All cameras</option>
              {cameras.map((cam) => (
                <option key={cam.id} value={String(cam.id)}>
                  {formatCameraLabel(cam, cam.id)}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="success"
              size="sm"
              onClick={() => setIsCreating(true)}
            >
              <FontAwesomeIcon icon={["fas", "plus"]} /> New Broadcast
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchBroadcasts(1)}
              disabled={isLoadingBroadcasts}
            >
              {isLoadingBroadcasts ? (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              ) : (
                <FontAwesomeIcon icon={["fas", "rotate"]} />
              )}
            </Button>
          </div>

          {/* Table */}
          <div className="flex-grow-1 overflow-auto">
            <Table table={table} />
            {broadcasts.length < broadcastsCount && (
              <div className="tw-w-full tw-flex tw-justify-center tw-mt-3">
                <div className="tw-max-w-[400px] tw-w-full">
                  <Button className="tw-w-full" onClick={loadMore} disabled={isLoadingBroadcasts}>
                    {isLoadingBroadcasts ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="tw-flex-1">
          {selectedBroadcast && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <VideoPlayer
                src={selectedBroadcast.url}
                ref={videoRef}
                fps={1 / frameTime}
                hlsUtcStartTime={selectedBroadcast.start}
                videoProps={{ autoPlay: false, loop: false, muted: false }}
                onVideoLoadHandler={onVideoLoadHandler}
              />
              <div className="tw-w-full tw-flex tw-justify-center">
                <Form.Select
                  value={pipelinePassing}
                  onChange={(e) => setPipelinePassing(e.target.value)}
                  className="tw-w-auto"
                >
                  {PIPELINE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="tw-w-full tw-flex tw-justify-center tw-items-end tw-gap-2">
                <Form.Group className="tw-w-full tw-max-w-xs">
                  <Form.Label>Order</Form.Label>
                  <InfiniteScrollSelect
                    options={orders}
                    value={selectedOrder?.id ?? null}
                    onChange={(e) => {
                      const order = orders.find((o) => o.id === e.target.value) ?? null;
                      setSelectedOrder(order);
                      setHomeTeamColor("");
                      setGuestTeamColor("");
                    }}
                    placeholder="Select order..."
                    getOptionValue={(o) => o.id}
                    getOptionLabel={(o) => `#${o.id} — ${o.order_type}`}
                    hasMore={ordersHasMore}
                    isLoading={ordersLoading}
                    onLoadMore={loadMoreOrders}
                  />
                </Form.Group>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => fetchOrders(1, false)}
                  disabled={ordersLoading}
                  className="tw-h-[38px]"
                  title="Reload orders"
                >
                  {ordersLoading ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  ) : (
                    <FontAwesomeIcon icon={["fas", "rotate"]} />
                  )}
                </Button>
              </div>
              <div className="tw-flex tw-justify-center tw-gap-4 tw-flex-wrap">
                <Form.Group>
                  <div className="tw-flex tw-justify-between">
                    <Form.Label>Start Time</Form.Label>
                    <Button
                      className="tw-text-xs tw-h-6 tw-rounded-md tw-flex gap-1"
                      variant="success"
                      size="sm"
                      onClick={setCurrentTimeAsStart}
                    >
                      SET TIME
                      <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                    </Button>
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="00:00:00"
                    value={startTime || ""}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <div className="tw-flex tw-justify-between">
                    <Form.Label>End Time</Form.Label>
                    <Button
                      className="tw-text-xs tw-h-6 tw-rounded-md tw-flex gap-1"
                      variant="success"
                      size="sm"
                      onClick={setCurrentTimeAsEnd}
                    >
                      SET TIME
                      <FontAwesomeIcon icon={["fas", "arrow-down"]} />
                    </Button>
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="00:00:00"
                    value={endTime || ""}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Match time</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    placeholder="Enter match time"
                    value={matchTime ?? ""}
                    onChange={(e) => setMatchTime(e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Left side jersey color</Form.Label>
                  <Form.Select
                    value={homeTeamColor}
                    onChange={(e) => setHomeTeamColor(e.target.value)}
                    disabled={!selectedOrder}
                  >
                    <option value="">Select color</option>
                    {[selectedOrder?.home_team?.color, selectedOrder?.guest_team?.color]
                      .filter(Boolean)
                      .map((colorValue) => {
                        const colorObj = TEAM_COLORS.find((c) => c.value === colorValue);
                        return (
                          <option key={colorValue} value={colorValue}>
                            {colorObj ? colorObj.label : colorValue}
                          </option>
                        );
                      })}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Right side jersey color</Form.Label>
                  <Form.Select
                    value={guestTeamColor}
                    onChange={(e) => setGuestTeamColor(e.target.value)}
                    disabled={!selectedOrder}
                  >
                    <option value="">Select color</option>
                    {[selectedOrder?.home_team?.color, selectedOrder?.guest_team?.color]
                      .filter(Boolean)
                      .map((colorValue) => {
                        const colorObj = TEAM_COLORS.find((c) => c.value === colorValue);
                        return (
                          <option key={colorValue} value={colorValue}>
                            {colorObj ? colorObj.label : colorValue}
                          </option>
                        );
                      })}
                  </Form.Select>
                </Form.Group>
              </div>
              <Button
                className="tw-w-full"
                variant="success"
                onClick={cutAndCreate}
                disabled={isCutting}
              >
                {isCutting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={["fas", "scissors"]} /> Cut and Create New Task
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create Broadcast Modal */}
      <Modal show={isCreating} onHide={handleCreateCancel}>
        <Modal.Header closeButton>
          <Modal.Title>New Broadcast</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label>Choose Arena</Form.Label>
            <Form.Select
              value={createArena}
              onChange={(e) => {
                setCreateArena(e.target.value);
                setCreatePlaygroundId("");
              }}
            >
              <option value="">-- Select Arena --</option>
              {[
                ...new Map(
                  cameras
                    .filter((c) => c.arena_id != null)
                    .map((c) => [c.arena_id, c.arena_name])
                ).entries(),
              ].map(([arenaId, arenaName]) => (
                <option key={arenaId} value={String(arenaId)}>
                  {arenaName}
                </option>
              ))}
              <option value="no-arena">No Arena</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Choose Field</Form.Label>
            <Form.Select
              value={createPlaygroundId}
              onChange={(e) => setCreatePlaygroundId(e.target.value)}
              disabled={!createArena}
            >
              <option value="">-- Select Field --</option>
              {cameras
                .filter((c) =>
                  createArena === "no-arena"
                    ? c.arena_id == null
                    : String(c.arena_id) === createArena
                )
                .map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name ?? c.id}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Start (UTC)</Form.Label>
            <Form.Control
              type="datetime-local"
              value={createStart}
              onChange={(e) => setCreateStart(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Duration (optional, defaults to 1 hour)</Form.Label>
            <div className="tw-flex tw-gap-2">
              <div className="tw-flex tw-items-center tw-gap-1">
                <Form.Control
                  type="number"
                  min={0}
                  placeholder="0"
                  value={createDurationHours}
                  onChange={(e) => setCreateDurationHours(e.target.value)}
                  style={{ width: 80 }}
                />
                <span>h</span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <Form.Control
                  type="number"
                  min={0}
                  max={59}
                  placeholder="0"
                  value={createDurationMinutes}
                  onChange={(e) => setCreateDurationMinutes(e.target.value)}
                  style={{ width: 80 }}
                />
                <span>min</span>
              </div>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCreateCancel}>
            Cancel
          </Button>
          <Button
            variant="success"
            disabled={isSubmitting || !createPlaygroundId || !createStart}
            onClick={handleCreate}
          >
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{" "}
                Creating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={["fas", "broadcast-tower"]} /> Create Broadcast
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LiveTab;
